import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { AngleDown } from '@zooplus/icons';
import { getTestLabels } from '../../../utils/getTestLabels.js';
import { Icon } from '../../Icon.js';
import { QuantityLoader } from '../QuantityLoader.js';
import style from './QuantityPicker.module.scss.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('QuantityPicker');
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=2884-21884&node-type=section&t=L12XIxh9aK1lGzgC-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Quantity/QuantityPicker.tsx)
 *
 * @param {boolean} isLoading - (optional) Can be set to `true` to show a loading indicator.
 * @param {function} onValueChange - (required) A custom function to call when the input value is changed. Recieves the selected quantity as `value` (number). Use from the outside to update the `value` prop.
 * @param {number[]} options - (required) An array of numbers representing possible purchase quantities.
 * @param {string} selectLabel - (optional, **Recommended**) An accessible label for the select element. **Automated accessibility tests will fail if a `selectLabel` is not provided!**
 * @param {object} selectProps - (optional) An object of native HTML Select element attributes ([see MDN docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select)) to pass to the select element.
 * @param {string} size - (optional) Controls the button size affecting its height, width and icon size. Can be one of `large` (default) or `small`.
 * @param {number} value - (required) Used to set an initial value. Update from the outside using `onValueChange`.
 */
const QuantityPicker = forwardRef((props, ref) => {
    const { className, isLoading = false, onValueChange, selectLabel, selectProps, options, size = 'large', value, ...otherProps } = props;
    const handleOptionChange = (event) => {
        onValueChange(parseInt(event.target.value));
    };
    return (jsxs("div", { "data-zta": componentTestLabel, ...otherProps, className: classNames(style['z-qty-picker'], {
            [`z-qty-picker--size-${size}`]: size !== 'large',
            [style['z-qty-picker--loading']]: isLoading,
        }, className), children: [jsx(Icon, { className: style['z-qty-picker__arrow'], children: jsx(AngleDown, { "aria-hidden": true }) }), jsx("select", { "data-zta": elementTestLabel('Select'), ...selectProps, ref: ref, value: value, className: classNames(style['z-qty-picker__select'], selectProps?.className), "aria-label": selectLabel || selectProps?.['aria-label'], onChange: handleOptionChange, children: options.map((option) => (jsx("option", { value: option, children: option }, option))) }), isLoading && jsx(QuantityLoader, { size: size })] }));
});
QuantityPicker.displayName = 'QuantityPicker';
/**
 * @deprecated use QuantityPicker instead
 */
const SelectQuantity = QuantityPicker;

export { QuantityPicker, SelectQuantity };
//# sourceMappingURL=QuantityPicker.js.map
